import React, {useCallback, useMemo, useState, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrFilterAccordion, {
  ActiveFilter,
} from '../../../common/arr-filter-panel/filter-accordion/ArrFilterAccordion';
import {ArrFilterAccordionData} from '../../../../shared/interfaces';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {
  cleanedIds,
  handleApplyUtil,
  resolveIdFromFixedData,
} from '../../../../utils/reportUtils';
import {
  useSubtallyReport,
  useTallyReport,
} from '../../../../hooks/reporting/useReporting';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {updateTally} from '../../../../redux/slice/tallyReportSlice';
import {clearSelectedAction} from '../../../../redux/slice/actionButtonSlice';
import {RootState} from '../../../../redux/store';
import {useLocation} from 'react-router-dom';
import {getTruncatedLabelWithPrefix} from '../../../../utils/utils';

export type SelectionItem = {
  id: string;
  label: string;
  data?: any[];
  type: string;
  disabled?: boolean;
  [key: string]: any;
};

interface Tag {
  id: string;
  label: string;
  children?: Tag[];
}

type TempSelect = {
  [key: string]: string[];
};

interface SelectionProps {
  title: string;
  disabled?: boolean;
  disableButton?: boolean;
  btnLabel: string;
  titleAlwaysEnabled?: boolean;
  sourceData?: any;
  selectionData: SelectionItem[];
  filterData?: ArrFilterAccordionData[];
  setSubSelectionData: (value: SelectionItem[]) => void;
  onStartLoading?: () => void;
  onStopLoading?: () => void;
  selectionResponse: (selectionType: string, selectionResponse: any) => void;
  selectionChanged: (value: string) => void;
  preSelectedData?: any;
}

const SelectionBy: React.FC<SelectionProps> = ({
  title,
  disabled = false,
  disableButton = false,
  titleAlwaysEnabled = false,
  btnLabel,
  sourceData,
  selectionData,
  filterData = [],
  setSubSelectionData,
  onStartLoading,
  onStopLoading,
  selectionResponse,
  selectionChanged,
  preSelectedData,
}) => {
  const isTitleDisabled = disabled && !titleAlwaysEnabled;
  const isButtonDisabled = disabled || disableButton;
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState<ISelectItem | any>(null);
  const [confirmedData, setConfirmedData] = useState<ISelectItem | any>(null);
  const [tempSelect, setTempSelect] = useState<TempSelect>({});
  const [treeSelect, setTreeSelect] = useState<any>([]);
  const [isSelectDisabled, setIsSelectDisabled] = useState(true);
  const [currentDrawerId, setCurrentDrawerId] = useState<number>(0);
  const [confirmedLabel, setConfirmedLabel] = useState<string | null>(null);
  const [confirmedFilters, setConfirmedFilters] = useState<ActiveFilter[]>([]);
  const [applyTrigger, setApplyTrigger] = useState(0);
  const {mutateAsync: fetchTallyReport} = useTallyReport();
  const {mutateAsync: fetchSubtallyReport} = useSubtallyReport();
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const dispatch = useDispatch();
  const applyType = titleAlwaysEnabled ? 'tally' : 'subtally';
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const tallyStoreData = useSelector(
    (state: RootState) => state?.tallyreport?.tallyData
  );
  const tallyStoreResults = useSelector(
    (state: RootState) => state?.tallyreport?.tallyResult
  );
  const tallyByCriteriaList = useSelector(
    (state: RootState) => state?.tallyreport?.tallyResult?.list
  );
  const [preActiveFilters, setPreActiveFilters] = useState<any>();

  const [labelWidth, setLabelWidth] = useState<number>(200);

  const selectionOptions: SelectionItem[] = useMemo(() => {
    return selectionData.map((s) => ({
      id: s.id,
      label: s.label,
      type: s.type,
      disabled: s.disabled ?? false,
      key: s.key,
      data: s.data,
    }));
  }, [selectionData]);

  const location = useLocation();

  const [isPreselectionApplied, setIsPreselectionApplied] = useState(false);

  const normalizeId = (id: string, prefix?: string) => {
    if (!id) return '';
    const trimmed = id.trim();

    const dropPrefix = (s: string) =>
      prefix && s.startsWith(prefix) ? s.slice(prefix.length) : s;

    const stripNonAlphanum = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '');

    return stripNonAlphanum(dropPrefix(trimmed)).toUpperCase();
  };

  const sectionContainsAnyId = (
    section: {data?: any[]; key?: string[]},
    ids: string[]
  ): boolean => {
    if (!Array.isArray(section.data) || ids.length === 0) return false;

    const keyPrefix = Array.isArray(section.key) ? section.key[0] : undefined;
    const selected = new Set(ids.map((id) => normalizeId(id, keyPrefix)));

    const stack: any[] = [...section.data];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;

      const nodeIdNorm = normalizeId(String(node.id ?? ''), keyPrefix);
      if (selected.has(nodeIdNorm)) {
        return true;
      }
      if (Array.isArray(node.children) && node.children.length) {
        stack.push(...node.children);
      }
    }
    return false;
  };

  const findSectionForIds = (sections: any[], ids: string[]): any | null => {
    if (!Array.isArray(sections) || ids.length === 0) return null;

    const ordered = sections
      .filter((s) => !s.disabled)
      .concat(sections.filter((s) => s.disabled));

    for (const section of ordered) {
      if (sectionContainsAnyId(section, ids)) {
        return section;
      }
    }
    return null;
  };

  const urlReportId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const id = q.get('ReportID');
    return id ? Number(id) : null;
  }, [location.search]);

  const preReportId = urlReportId;

  const prePopulateData = async () => {
    if (!preSelectedData) {
      setIsPreselectionApplied(false);
      return;
    }

    if (preReportId != preSelectedData.reportId) return;

    onStartLoading?.();

    try {
      await new Promise((r) => setTimeout(r, 0));
      if (applyType == 'tally') {
        const tallyData = preSelectedData.tallyFilter.tally;
        const key = tallyData.tallyByType;
        const keyid =
          tallyData.tallyByID == '' || !tallyData.tallyByID
            ? tallyData.tallyByIDExceptMetadata
            : tallyData.tallyByID;

        const tallyIdx = {key: key, id: keyid};
        const getPreTallySelectId = resolveIdFromFixedData(
          selectionData,
          tallyIdx
        );
        if (!getPreTallySelectId) return;

        const preTallyIds = getPreTallySelectId.formatted;
        const matchedSection =
          findSectionForIds(selectionOptions, preTallyIds) ??
          selectionOptions.find((s) => !s.disabled) ??
          selectionOptions[0];
        if (!matchedSection) return;

        const nextTemp: TempSelect = {[matchedSection.id]: [...preTallyIds]};
        const nextTree = [...preTallyIds];

        setDrawerData(matchedSection);
        setTreeSelect(nextTree);
        setTempSelect(nextTemp);

        const formattedLabel = formatDropdownLabel(matchedSection, nextTemp);

        setIsApplyDisabled(false);
        setConfirmedData({...matchedSection, label: formattedLabel});

        const activeFilters = tallyData.filterByMetaData.length
          ? tallyData.filterByMetaData
          : [];
        setPreActiveFilters(activeFilters);
      } else if (applyType == 'subtally') {
        const subtallyData = preSelectedData.tallyFilter.subTally;

        const key = subtallyData.tallyByType;
        const keyid =
          subtallyData.tallyByID == '' || !subtallyData.tallyByID
            ? subtallyData.subTallyByIDExceptMetadata
            : subtallyData.tallyByID;

        if (!key || !keyid) return;
        const subtallyIdx = {key: key, id: keyid};
        const getPreTallySelectId = resolveIdFromFixedData(
          selectionData,
          subtallyIdx
        );
        if (!getPreTallySelectId) return;

        const preTallyIds = getPreTallySelectId.formatted;
        const matchedSection =
          findSectionForIds(selectionOptions, preTallyIds) ??
          selectionOptions.find((s) => !s.disabled) ??
          selectionOptions[0];
        if (!matchedSection) return;

        const nextTemp: TempSelect = {[matchedSection.id]: [...preTallyIds]};
        const nextTree = [...preTallyIds];

        setDrawerData(matchedSection);
        setTreeSelect(nextTree);
        setTempSelect(nextTemp);

        const formattedLabel = formatDropdownLabel(matchedSection, nextTemp);

        setIsApplyDisabled(false);
        setConfirmedData({...matchedSection, label: formattedLabel});

        const activeFilters = subtallyData.filterByMetaData.length
          ? subtallyData.filterByMetaData
          : [];
        setPreActiveFilters(activeFilters);
      }
    } finally {
      setIsPreselectionApplied(true);
      onStopLoading?.();
    }
  };

  useEffect(() => {
    if (isPreselectionApplied && preSelectedData?.tallyFilter) {
      const idsCsv = cleanedIds(treeSelect);
      const strippedIds = idsCsv ? idsCsv.split(',') : [];

      const prefix = (confirmedData?.key?.[0] ?? '').trim();
      const sourceSections = (confirmedData?.data ??
        drawerData?.data ??
        []) as Tag[];

      const strippedMap = buildIdLabelmap(sourceSections, prefix);

      const idLabels = strippedIds.map((id) => ({
        Id: id.trim(),
        Label: strippedMap[id.trim()] ?? '',
      }));

      const preTallyInfo = preSelectedData.tallyFilter.tally;
      const preSubTallyInfo = preSelectedData.tallyFilter.subTally;

      if (applyType == 'tally') {
        dispatch(
          updateTally({
            selectedSource: Object.entries(sourceData)[0][1],
            tallyData: {
              id:
                preTallyInfo.tallyByID == '' || !preTallyInfo.tallyByID
                  ? preTallyInfo.tallyByIDExceptMetadata
                  : preTallyInfo.tallyByID,
              type: preTallyInfo.tallyByType ?? null,
              filters: {...confirmedFilters},
              label: selectedLabel,
              idLabels: idLabels,
            },
            tallyResponse: {
              reportId: preSelectedData.reportId ?? null,
              notificationid: 0,
              reportPath: preSelectedData.tallyFilter.reportFilePath ?? null,
              reportName: preSelectedData.tallyFilter.reportName ?? null,
              reportType: preSelectedData.repTypes ?? null,
            },
            tallyResult: {
              dataasof: preSelectedData.tallyFilter?.dataAsOf ?? '',
              list: preTallyInfo?.tallyByCriteriaList ?? [],
              docs: [],
            },
          })
        );
      } else if (applyType == 'subtally') {
        dispatch(
          updateTally({
            subtallyData: {
              id:
                preSubTallyInfo?.tallyByID == '' || !preSubTallyInfo?.tallyByID
                  ? preSubTallyInfo?.subTallyByIDExceptMetadata
                  : preSubTallyInfo?.tallyByID,
              type: preSubTallyInfo?.tallyByType ?? null,
              filters: {...confirmedFilters},
              label: selectedLabel,
              idLabels: idLabels,
            },
            subtallyResponse: {
              reportId: preSelectedData.reportId ?? null,
              notificationid: 0,
              reportPath: preSelectedData.tallyFilter.reportFilePath ?? null,
              reportName: preSelectedData.tallyFilter.reportName ?? null,
              reportType: preSelectedData.repTypes ?? null,
            },
            subtallyResult: {
              dataasof: preSelectedData.tallyFilter?.dataAsOf ?? '',
              list: preSubTallyInfo?.tallyByCriteriaList ?? [],
              docs: [],
            },
          })
        );
      }
    }
  }, [isPreselectionApplied, preSelectedData, confirmedFilters]);

  useEffect(() => {
    if (
      !isPreselectionApplied &&
      preSelectedData &&
      selectionOptions.length > 0 &&
      selectionData.length > 0
    ) {
      (async () => {
        await prePopulateData();
      })();
    }
  }, [isPreselectionApplied, preSelectedData, selectionOptions, selectionData]);

  useEffect(() => {
    setConfirmedData(null);
    setDrawerData(null);
    setIsApplyDisabled(true);
    setIsSelectDisabled(true);
    setConfirmedData(null);
    setTempSelect({});
    setTreeSelect([]);
    setCurrentDrawerId(0);
    setConfirmedLabel(null);
    setConfirmedFilters([]);
    setPreActiveFilters(null);
    dispatch(clearSelectedAction());
    setIsPreselectionApplied(false);

    if (preSelectedData && selectionOptions.length > 0) {
      (async () => {
        await prePopulateData();
      })();
    }
  }, [preSelectedData, selectionOptions, dispatch, isButtonDisabled]);

  useEffect(() => {
    selectionChanged(applyType);
  }, [confirmedFilters, applyTrigger]);

  const noSelectedInfo =
    applyType === 'tally'
      ? false
      : tallyStoreResults?.docs.length > 0
        ? false
        : true;

  const selectedSource = (Object.values(sourceData ?? {})[0] as any[])?.[0];

  const filterSelectionData = (selectedIds?: string[]) => {
    const ids = selectedIds ?? treeSelect;
    if (!Array.isArray(selectionData)) return;
    const filterChildren = (items: any[]): any[] =>
      items
        .map((item) => ({
          ...item,
          children: item.children ? filterChildren(item.children) : undefined,
        }))
        .filter((item) => {
          const isSelected = ids.includes(item.id);
          const hasChildren = item.children?.length > 0;
          return !isSelected && (hasChildren || !item.children);
        });
    const filteredData = selectionData.map((section) => {
      const newSection = {
        ...section,
        data: section.data ? filterChildren(section.data) : [],
      };
      newSection.disabled = newSection.data.length === 0;
      return newSection;
    });
    setSubSelectionData(filteredData);
  };

  const formatDropdownLabel = (
    section?: {id: string; label: string; data?: Tag[]},
    localTempSelect?: TempSelect
  ): string => {
    const currentDrawer = section ?? drawerData;
    const temp = localTempSelect ?? tempSelect;

    if (!currentDrawer) return btnLabel;

    const prefix = `${currentDrawer.label}:`;
    const collectLabels = (nodes: Tag[]): [string[], boolean] => {
      const labels: string[] = [];
      let allSelected = true;

      for (const node of nodes) {
        if (node.children && node.children.length > 0) {
          const [childLabels, childFullySelected] = collectLabels(
            node.children
          );
          if (childFullySelected) {
            labels.push(node.label);
          } else {
            labels.push(...childLabels);
            allSelected = false;
          }
        } else {
          if (temp[currentDrawer.id]?.includes(node.id)) {
            labels.push(node.label);
          } else {
            allSelected = false;
          }
        }
      }
      return [labels, allSelected];
    };

    const finalLabels: string[] = [];
    for (const parent of currentDrawer.data ?? []) {
      const [labels, allSelected] = collectLabels([parent]);
      if (allSelected) {
        finalLabels.push(parent.label);
      } else {
        finalLabels.push(...labels);
      }
    }

    const caption = getTruncatedLabelWithPrefix(
      prefix,
      finalLabels,
      labelWidth
    );
    setSelectedLabel(currentDrawer.label);
    setConfirmedLabel(caption);

    return caption;
  };

  const handleSelection = (value: ISelectItem | null) => {
    setDrawerData(value);
    setCurrentDrawerId((prev) => prev + 1);
    setIsDrawerOpen(true);
    setIsSelectDisabled(true);

    if (value?.id != null && confirmedData?.id === value.id) {
      setConfirmedData((prev: ISelectItem | null) => (prev ? {...prev} : prev));
    }
  };

  const handleDrawerSelect = () => {
    const filteredIds = [...Object.values(tempSelect)[0]].filter((item) =>
      item.includes(drawerData.key[0])
    );
    setTreeSelect([...filteredIds]);
    if (tempSelect[drawerData.id]) {
      setIsApplyDisabled(false);
    } else {
      setIsApplyDisabled(true);
    }
    const formattedLabel = formatDropdownLabel();
    setConfirmedData({...drawerData, label: formattedLabel});
    selectionChanged(applyType);
    setIsDrawerOpen(false);
  };

  const handleTreeSelected = (values: string[] | null) => {
    if (!values || values.length === 0) {
      setIsSelectDisabled(true);
      return;
    }
    const drawerId = drawerData?.id ?? '';
    if (treeSelect.length > 0) {
      const confirmedId = confirmedData?.id ?? '';
      if (drawerId === confirmedId) {
        setTempSelect({[confirmedId]: values ?? []});
      } else {
        // eslint-disable-next-line security/detect-object-injection
        const toRemove = new Set(tempSelect[confirmedId] ?? []);
        const newSelection =
          values?.filter((item) => !toRemove.has(item)) ?? [];

        setTempSelect({[drawerId]: newSelection});
        if (newSelection.length === 0) {
          setIsSelectDisabled(true);
          return;
        }
      }
    } else {
      setTempSelect({[drawerId]: values ?? []});
    }
    setIsSelectDisabled(false);
  };

  const handleCancel = () => {
    setConfirmedData({...drawerData, label: confirmedLabel ?? btnLabel});
    setIsDrawerOpen(false);
    setIsSelectDisabled(true);
  };

  const buildIdLabelmap = (sections: Tag[] = [], prefix = '') => {
    const map: Record<string, string> = {};

    const stripPrefixOnly = (id: string) => {
      const trimmed = id.trim();
      if (!prefix || !trimmed.startsWith(prefix)) return trimmed;
      return trimmed.slice(prefix.length);
    };

    const walk = (node: Tag) => {
      map[stripPrefixOnly(node.id)] = node.label;
      if (node.children?.length) node.children.forEach(walk);
    };

    sections.forEach(walk);
    return map;
  };

  const storeSelectionData = (data: any) => {
    const idsCsv = cleanedIds(treeSelect);
    const strippedIds = idsCsv ? idsCsv.split(',') : [];

    const prefix = (confirmedData?.key?.[0] ?? '').trim();
    const sourceSections = (confirmedData?.data ??
      drawerData?.data ??
      []) as Tag[];

    const strippedMap = buildIdLabelmap(sourceSections, prefix);

    const idLabels = strippedIds.map((id) => ({
      Id: id.trim(),
      Label: strippedMap[id.trim()] ?? '',
    }));

    if (applyType === 'tally') {
      const modelData = data?.backgroundModelData ?? {};
      dispatch(
        updateTally({
          selectedSource: Object.entries(sourceData)[0][1],
          tallyData: {
            id: idsCsv,
            type: confirmedData.id,
            filters: {...confirmedFilters},
            label: selectedLabel,
            idLabels,
          },
          tallyResponse: {
            reportId: modelData.reportID,
            notificationid: modelData.notificationID,
            reportPath: modelData.reportFilePath,
            reportName: modelData.reportName,
            reportType: modelData.reportType,
          },
        })
      );
    } else {
      const modelData = data?.shareReportData ?? {};
      dispatch(
        updateTally({
          subtallyData: {
            id: idsCsv,
            type: confirmedData.id,
            filters: {...confirmedFilters},
            label: selectedLabel,
            idLabels,
          },
          subtallyResponse: {
            reportId: modelData.reportID,
            notificationid: modelData.notificationID,
            reportPath: modelData.reportFilePath,
            reportName: modelData.reportName,
            reportType: modelData.reportType,
          },
        })
      );
    }
  };

  const handleApply = useCallback(async () => {
    setApplyTrigger((n) => n + 1);
    onStartLoading?.();
    const selectedInfo = {
      tallyId: [tallyStoreData?.id],
      tallyName: tallyStoreData?.type,
      tallyFilters: tallyStoreData?.filters,
      barResult: tallyStoreResults?.docs,
    };

    const {success, data} = await handleApplyUtil({
      selectedSource,
      treeSelect,
      confirmedData,
      applyType,
      selectedInfo,
      confirmedFilters,
      fetchTallyReport,
      fetchSubtallyReport,
      tallyByCriteriaList,
    });

    if (success && data) {
      storeSelectionData(data);
      selectionResponse(applyType, data);
      if (applyType === 'tally') {
        filterSelectionData();
      }
    } else {
      setShowErrorToast(true);
      onStopLoading?.();
    }
  }, [
    selectedSource,
    treeSelect,
    confirmedData,
    confirmedFilters,
    fetchTallyReport,
    fetchSubtallyReport,
    onStartLoading,
  ]);

  const setAnchorElementWidth = (width: number) => {
    setLabelWidth(width);
  };
  const renderContent = useMemo(() => {
    if (!drawerData) return;
    const isSingle = drawerData.type !== 'multi';
    return (
      <div
        className="arr-tree-container"
        data-testid="arr-singleselect-tree-view"
        aria-label={`${drawerData?.label} data`}
        aria-live="polite"
        aria-atomic="false"
        tabIndex={0}
      >
        <ArrTreeView
          key={`selectionby-tree-${drawerData.id}-${currentDrawerId}`}
          setSelectedItem={handleTreeSelected}
          itemsList={drawerData?.data}
          selectedItem={treeSelect[0]}
          treeSelectedItems={treeSelect}
          handleNoResultMessage={`${drawerData?.label} data is loading...`}
          allowGlobalSelect={true}
          multipleSelection={!isSingle}
          isShowCountLabel={false}
          isShowCheckbox={!isSingle}
          isChildrenIndendation={true}
        />
      </div>
    );
  }, [drawerData, treeSelect, currentDrawerId]);

  const drawerContent = (
    <Box className="drawer-container">
      <Box className="drawer-header-box">
        <Typography
          className=" typo-text subhead5_size small-title"
          aria-label={title}
          aria-live="polite"
          aria-atomic="false"
          data-testid={`selection-by-title`}
          tabIndex={0}
        >
          {title}:
        </Typography>
        <Box className="drawer-row">
          <Typography
            className=" typo-text headline3_size title"
            aria-label={drawerData?.label}
            aria-live="polite"
            aria-atomic="false"
            data-testid={`selection-by-label`}
            tabIndex={0}
          >
            {drawerData?.label}
          </Typography>
          <Box className="drawer-buttons-box">
            <ArrButton
              aria-label={t('common.cancel')}
              aria-live="polite"
              aria-atomic="false"
              variant="secondary"
              size="medium"
              onClick={() => {
                handleCancel();
              }}
              disabled={false}
              dataTestId="selection-by-cancel-button"
              className="cancel-button"
              tabIndex={0}
            >
              {t('common.cancel')}
            </ArrButton>
            <ArrButton
              aria-label={t('common.select')}
              aria-live="polite"
              aria-atomic="false"
              aria-disabled={isSelectDisabled}
              variant="primary"
              size="medium"
              onClick={() => {
                handleDrawerSelect();
              }}
              disabled={isSelectDisabled}
              dataTestId="select-button"
              className="select-button"
              tabIndex={0}
            >
              {t('common.select')}
            </ArrButton>
          </Box>
        </Box>
      </Box>
      <Box
        className="content-box"
        aria-label="tree content"
        aria-live="polite"
        aria-atomic="false"
        tabIndex={0}
      >
        {renderContent}
      </Box>
    </Box>
  );
  return (
    <Box data-testid="selectionby-container" className="selectionby-container">
      <Box
        data-testid="selectionby-top"
        className="selectionby-top"
        tabIndex={0}
      >
        <div
          className="selectionby-top-label"
          aria-label={title}
          aria-live="polite"
          aria-atomic="false"
          data-testid={`selection-by-div`}
          tabIndex={0}
        >
          <Typography
            className={`typo-text subhead4_size ${isTitleDisabled ? 'disabled' : ''}`}
            data-testid="selectionby-title"
            tabIndex={0}
          >
            {title}
          </Typography>
        </div>
        <div
          className="selectionby-top-buttongroup"
          aria-label={title}
          aria-live="polite"
          aria-atomic="false"
          data-testid={`selection-by-apply`}
          tabIndex={0}
        >
          <ArrSingleSelectDropdown
            key={`${applyType}-afa-${preReportId}`}
            aria-disabled={isButtonDisabled}
            aria-live="polite"
            aria-atomic="false"
            dataTestId="asd-selectionby-top-ddapply"
            aria-label={btnLabel}
            dropdownLabel={btnLabel}
            dropdownItemKey="id"
            dropdownItemDisplay="label"
            dropdownItems={selectionOptions}
            selectedValues={confirmedData}
            onSelectionChange={(val: ISelectItem | null) =>
              handleSelection(val)
            }
            disabled={isButtonDisabled}
            sizeVariant="medium"
            setAnchorElementWidth={setAnchorElementWidth}
            tabIndex={-1}
          />
          <ArrButton
            aria-disabled={
              isApplyDisabled || selectionData.length === 0 || noSelectedInfo
            }
            aria-label={t('common.button.apply')}
            aria-live="polite"
            aria-atomic="false"
            dataTestId="tally-selection-apply-button"
            variant="primary"
            size="small"
            onClick={() => handleApply()}
            disabled={
              isApplyDisabled || selectionData.length === 0 || noSelectedInfo
            }
            className="apply-button"
            tabIndex={0}
          >
            {t('common.button.apply')}
          </ArrButton>
          <ArrDrawer
            aria-label="drawer"
            aria-live="polite"
            aria-atomic="false"
            showButton={false}
            title={title}
            isOpen={isDrawerOpen}
            size="small"
            side="right"
            onClose={() => {}}
            showHeader={false}
            trapFocus={true}
            data-testid="arr-selection-drawer"
          >
            {drawerContent}
          </ArrDrawer>
        </div>
      </Box>
      <Box
        data-testid="filter-panel"
        className="filter-panel"
        aria-live="polite"
        aria-atomic="false"
      >
        {!disabled && (
          <ArrFilterAccordion
            key={`${applyType}-afa-${preReportId}`}
            aria-disabled={isApplyDisabled}
            filters={filterData}
            isFilterInactive={isApplyDisabled}
            aria-label={t('reports.tally.filterPanel.title')}
            title={t('reports.tally.filterPanel.title')}
            expand={true}
            isApplyFilterButtonVisible={false}
            onSetActiveFilters={(filters) => {
              setConfirmedFilters(filters);
            }}
            preActiveFilters={preActiveFilters}
            dataTestId="tally-filter-panel-accordion"
          />
        )}
      </Box>
      {showErrorToast && (
        <div data-testid="toast-notification" tabIndex={0}>
          <ArrToastNotification
            message={'Something went wrong.'}
            status={[ToastType.Error]}
            onClose={() => setShowErrorToast(false)}
          />
        </div>
      )}
    </Box>
  );
};

export default SelectionBy;
